import type { GridReadyEvent, StateUpdatedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { LoadingIndicator } from '@/components/LoadingIndicator/LoadingIndicator';
import { DEFAULT_TIME_FILTER } from '@/components/TimeFilter/TimeFilter.constants';
import { DEFAULT_PAGE_SIZE } from '@/constants/common';
import useGridTheme from '@/hooks/useGridTheme';
import useHandleError from '@/hooks/useHandleError';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import {
  isGroupedByVulnerabilityAtom,
  vulnerabilitiesFiltersAtom,
  vulnerabilitiesGridApiAtom,
  vulnerabilitiesTimeFilterAtom
} from '@/pages/VulnerabilityManagement/VMPVulnerabilities/VMPVulnerabilities.store';
import { getVulnerabilitiesServerSideDatasource } from '@/pages/VulnerabilityManagement/VulnerabilityManagement.store';
import { gridGetStoredState, gridSetStoreState } from '@/utils/Grid.utils';
import { loadingCellRenderer } from '@/utils/GridRenderer.utils';

import {
  getVulnerabilityColumnDefs,
  VULNERABILITIES_TABLE_DEFAULT_SORT_MODEL
} from './VulnerabilitiesTable.constants';
import useStyles from './VulnerabilitiesTable.styles';

const GRID_NAME = 'VulnerabilitiesTable';

const VulnerabilitiesTable = ({
  tableLocation
}: {
  tableLocation: 'asset' | 'vulnerabilities-tab';
}) => {
  const gridTheme = useGridTheme();
  const { classes } = useStyles();

  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const vulnerabilitiesFilters = useAtomValue(vulnerabilitiesFiltersAtom);
  const vulnerabilitiesTimeFilter = useAtomValue(vulnerabilitiesTimeFilterAtom);
  const handleError = useHandleError();
  const [vulnerabilitiesGridApi, setVulnerabilitiesGridApi] = useAtom(
    vulnerabilitiesGridApiAtom
  );
  const isGroupedByVulnerability = useAtomValue(isGroupedByVulnerabilityAtom);
  const [isFetching, setIsFetching] = useState(false);

  // Read once on mount. Deriving this from the grid api makes it change after
  // onGridReady, and any cacheBlockSize change resets the server-side store.
  const { initialState, pageSize } = useMemo(() => {
    const storedState = gridGetStoredState(
      GRID_NAME,
      VULNERABILITIES_TABLE_DEFAULT_SORT_MODEL
    );

    return {
      initialState: storedState,
      pageSize: storedState.pagination?.pageSize ?? DEFAULT_PAGE_SIZE
    };
  }, []);

  const cacheBlockSize = pageSize * 5;

  // The filter atoms hydrate from the URL/session only after the filter-config
  // request resolves; a null time filter means that hasn't happened yet.
  const filtersHydrated = vulnerabilitiesTimeFilter !== null;

  const vulnerabilitiesFiltersKey = JSON.stringify(vulnerabilitiesFilters);
  const vulnerabilitiesTimeFilterKey = JSON.stringify(
    vulnerabilitiesTimeFilter
  );
  const memoizedDatasource = useMemo(
    () =>
      getVulnerabilitiesServerSideDatasource(
        selectedOrganization.label as string,
        vulnerabilitiesFilters,
        vulnerabilitiesTimeFilter || DEFAULT_TIME_FILTER,
        handleError,
        setIsFetching,
        isGroupedByVulnerability
      ),
    // Serialize objects so useMemo compares by value, not reference.
    // vulnerabilitiesFilters and ticketTimeFilter are arrays/objects that may get new
    // references on every render even when the content hasn't changed.
    [
      selectedOrganization.label,
      vulnerabilitiesFiltersKey,
      vulnerabilitiesTimeFilterKey,
      isGroupedByVulnerability
    ]
  );

  useEffect(() => {
    if (!filtersHydrated) return;

    if (vulnerabilitiesGridApi && !vulnerabilitiesGridApi.isDestroyed()) {
      vulnerabilitiesGridApi.setGridOption(
        'serverSideDatasource',
        memoizedDatasource
      );
    }
  }, [filtersHydrated, memoizedDatasource, vulnerabilitiesGridApi]);

  const columnDefs = useMemo(
    () => getVulnerabilityColumnDefs(tableLocation, isGroupedByVulnerability),
    [tableLocation, isGroupedByVulnerability]
  );

  const handleStateUpdate = useCallback(
    (event: StateUpdatedEvent) => gridSetStoreState(event, GRID_NAME),
    []
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      setVulnerabilitiesGridApi(params.api);
    },
    [setVulnerabilitiesGridApi]
  );

  const gridOptions = useMemo(() => ({ initialState }), [initialState]);

  return (
    <div
      className={classes.assetsTableContainer}
      style={{ height: '100%', width: '100%' }}
    >
      <LoadingIndicator
        isLoading={isFetching || !filtersHydrated}
        noBackground
      />
      <AgGridReact
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        rowModelType="serverSide"
        pagination
        paginationPageSize={pageSize}
        cacheBlockSize={cacheBlockSize}
        onGridReady={onGridReady}
        theme={gridTheme}
        suppressCellFocus
        onStateUpdated={handleStateUpdate}
        rowHeight={55}
        loading={false}
        loadingCellRenderer={loadingCellRenderer}
      />
    </div>
  );
};

export default VulnerabilitiesTable;
